import {
  useCallback, useRef, useState,
} from 'react';
import { createUuid } from '~/_shared/utils/createUuid';
import { useAjaxCall } from '~/_shared/utils/hooks/useAjaxCall';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import {
  type CreateBoundaryGroupFromAIConfig,
  NumberOfTerritoriesLastUpdateReason, type SalesRepLocationsMap,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import { WMS_MIN_NUMBER_OF_TERRITORIES } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/additionalOptions.component';
import { useSpreadsheetRowCount } from '~/spreadsheet/useSpreadsheetRowCount';
import { getBoundaryGroupItemsList } from '~/store/boundaries/boundaries.repository';
import {
  type BoundaryGroupId, type BoundaryTerritoryGroupId,
} from '~/store/boundaries/boundaryIdentifier.type';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';

export type CreateOptimizedTerritoriesCountParams = Readonly<{
  previousTerritoriesBoundaryGroupId: BoundaryTerritoryGroupId | null;
  respectPrevTerritories: boolean;
  respectSalesLocations: boolean;
  salesRepLocationsMap: SalesRepLocationsMap | null;

  useNumberOfTerritories: boolean;
  numberOfSalesReps: number | null;
  numberOfPreviousTerritories: number | null;
}>;

type UseNumberOfTerritoriesCountParams = {
  config: CreateOptimizedTerritoriesCountParams;
  onNumberOfTerritoriesChange: (numberOfTerritories: number) => void;
  onNumberOfSalesRepChange: (numberOfSalesReps: number) => void;
};

const applyComputedTerritoriesCount = (config: CreateOptimizedTerritoriesCountParams, onNumberOfTerritoriesChange: (numberOfTerritories: number) => void) => {
  const numberOfTerritories = config.useNumberOfTerritories
    ? (config.respectSalesLocations ? config.numberOfSalesReps
      : (config.respectPrevTerritories ? config.numberOfPreviousTerritories
        : null)) : null;

  if (notNullOrUndefined(numberOfTerritories) && numberOfTerritories >= WMS_MIN_NUMBER_OF_TERRITORIES) {
    onNumberOfTerritoriesChange(numberOfTerritories);
  }
};

const fetchNumberOfPreviousTerritories = (clientId: number, boundaryGroupId: BoundaryGroupId) =>
  getBoundaryGroupItemsList(clientId, boundaryGroupId).then(boundaryList => boundaryList.length);

export const useNumberOfTerritoriesCount = ({ config, onNumberOfTerritoriesChange, onNumberOfSalesRepChange }: UseNumberOfTerritoriesCountParams) => {
  const clientId = useClientIdSelector();

  const [numberOfTerritoriesUpdateReason, setNumberOfTerritoriesUpdateReason] = useState(NumberOfTerritoriesLastUpdateReason.Other);
  const lastInvokeId = useRef<string | null>(null);

  const { isLoading: salesRepsCountLoading, getSpreadsheetRowCount: getSalesRepsCount } = useSpreadsheetRowCount();
  const { isLoading: previousTerritoriesCountLoading, invokeAjax: getPreviousTerritoriesCount } = useAjaxCall(fetchNumberOfPreviousTerritories);

  const refreshSalesRepsCount = useCallback((params?: {
    autoRefresh?: boolean;
    salesRepLocationsMap?: SalesRepLocationsMap | null;
  }) => {
    if (config.respectSalesLocations) {
      const salesRepLocationsMap = params?.salesRepLocationsMap ?? config.salesRepLocationsMap;
      if (!salesRepLocationsMap) {
        return;
      }

      const localInvokeId = createUuid();
      lastInvokeId.current = localInvokeId;

      getSalesRepsCount(salesRepLocationsMap).then(count => {
        if (localInvokeId !== lastInvokeId.current) {
          return;
        }

        if (params?.autoRefresh) {
          setNumberOfTerritoriesUpdateReason(NumberOfTerritoriesLastUpdateReason.SalesRepLocationsMapIdChange);
        }

        onNumberOfSalesRepChange(count);
        applyComputedTerritoriesCount(config, onNumberOfTerritoriesChange);
      });
    }
  }, [config, getSalesRepsCount, onNumberOfSalesRepChange, onNumberOfTerritoriesChange]);

  const refreshPreviousNumberOfTerritories = useCallback((params?: {
    autoRefresh?: boolean;
    previouslySavedBoundaryGroupId?: number | null;
  }) => {
    if (config.respectPrevTerritories) {
      const previouslySavedBoundaryGroupId = params?.previouslySavedBoundaryGroupId ?? config.previousTerritoriesBoundaryGroupId;
      if (!previouslySavedBoundaryGroupId || !clientId) {
        return;
      }

      const localInvokeId = createUuid();
      lastInvokeId.current = localInvokeId;

      getPreviousTerritoriesCount(clientId, previouslySavedBoundaryGroupId).then(count => {
        if (localInvokeId !== lastInvokeId.current) {
          return;
        }

        if (params?.autoRefresh) {
          setNumberOfTerritoriesUpdateReason(NumberOfTerritoriesLastUpdateReason.RespectPreviousTerritoriesChange);
        }

        onNumberOfSalesRepChange(count);
        applyComputedTerritoriesCount(config, onNumberOfTerritoriesChange);
      });
    }
  }, [clientId, config, getPreviousTerritoriesCount, onNumberOfSalesRepChange, onNumberOfTerritoriesChange]);

  const handleNumberOfTerritoriesChange = useCallback((update: Partial<CreateBoundaryGroupFromAIConfig>) => {
    const { salesRepLocationsMap, previouslySavedBoundaryGroupId, numberOfTerritories, respectSalesLocations } = update;

    if (numberOfTerritories !== undefined) {
      setNumberOfTerritoriesUpdateReason(NumberOfTerritoriesLastUpdateReason.ManualEdit);
    }

    if (salesRepLocationsMap) {
      refreshSalesRepsCount({ autoRefresh: true, salesRepLocationsMap });
    }

    if (previouslySavedBoundaryGroupId || (respectSalesLocations === false)) {
      refreshPreviousNumberOfTerritories({ autoRefresh: true, previouslySavedBoundaryGroupId });
    }
  }, [refreshPreviousNumberOfTerritories, refreshSalesRepsCount]);

  const onRefreshTerritoriesCount = useCallback(() => {
    if (config.respectSalesLocations) {
      refreshSalesRepsCount();
    }
    else {
      refreshPreviousNumberOfTerritories();
    }
  }, [config.respectSalesLocations, refreshPreviousNumberOfTerritories, refreshSalesRepsCount]);

  const numberOfTerritoriesLoading = (config.respectSalesLocations && salesRepsCountLoading)
    || (config.respectPrevTerritories && previousTerritoriesCountLoading);

  return {
    numberOfTerritoriesUpdateReason,
    onRefreshTerritoriesCount,
    handleNumberOfTerritoriesChange,
    numberOfTerritoriesLoading,
  };
};
